import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, RefreshCw, Search, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DdtDetail = ({ id, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/etifood-agent/ddt/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Errore")); }, [id]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="etifood-ddt-detail">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-[#8A1C2A]" /> DDT {d?.numero} del {fmtDate(d?.data)}</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-stone-500 py-4">Caricamento...</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div><span className="text-stone-500 text-xs block">Cliente</span><span className="font-semibold">{d.cliente_ragione_sociale || d.cliente_codice}</span></div>
              <div><span className="text-stone-500 text-xs block">P.IVA</span>{d.cliente_piva || "—"}</div>
              <div><span className="text-stone-500 text-xs block">Località</span>{d.cliente_localita} {d.cliente_provincia && `(${d.cliente_provincia})`}</div>
              <div><span className="text-stone-500 text-xs block">Tipo / Serie</span>{d.tipo}{d.serie ? `/${d.serie}` : ""}</div>
              <div><span className="text-stone-500 text-xs block">Trasporto</span>{fmtDate(d.data_trasporto)} · {d.aspetto_esteriore || "—"}</div>
              <div><span className="text-stone-500 text-xs block">Rif. ordine</span>{d.numero_ordine || "—"}</div>
            </div>
            {d.note && <p className="text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2"><strong>Note:</strong> {d.note}</p>}
            <div>
              <p className="font-semibold text-sm mb-2">Righe ({d.num_righe})</p>
              <div className="overflow-x-auto border border-stone-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-stone-50"><tr>
                    <th className="text-left px-3 py-2">Cod.</th><th className="text-left px-3 py-2">Descrizione</th>
                    <th className="text-right px-3 py-2">Q.tà</th><th className="text-right px-3 py-2">Prezzo</th>
                    <th className="text-right px-3 py-2">Sc.%</th><th className="text-left px-3 py-2">IVA</th>
                  </tr></thead>
                  <tbody>
                    {(d.righe || []).map((r, i) => (
                      <tr key={i} className="border-t border-stone-100">
                        <td className="px-3 py-1.5 font-mono">{r.codice_articolo}</td>
                        <td className="px-3 py-1.5">{r.descrizione}</td>
                        <td className="px-3 py-1.5 text-right">{r.quantita} {r.unita_misura}</td>
                        <td className="px-3 py-1.5 text-right">{eur(r.prezzo)}</td>
                        <td className="px-3 py-1.5 text-right">{r.sconto || 0}</td>
                        <td className="px-3 py-1.5">{r.codice_iva}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end gap-6 text-sm border-t border-stone-200 pt-3">
              <div><span className="text-stone-500 text-xs block">Imponibile</span><span className="font-semibold">{eur(d.totale_imponibile)}</span></div>
              <div><span className="text-stone-500 text-xs block">IVA</span>{eur(d.totale_iva)}</div>
              <div><span className="text-stone-500 text-xs block">Totale</span><span className="font-bold text-[#8A1C2A]">{eur(d.totale_documento)}</span></div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function EtifoodDDT() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/etifood-agent/ddt`, { params: { q: q || undefined, limit: 100 } });
      setData(r.data);
    } catch { /* ignore */ }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="etifood-ddt-page">
      <div>
        <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2"><Truck className="w-6 h-6 text-[#8A1C2A]" /> DDT importati da Etiflor</h1>
        <p className="text-sm text-stone-500 mt-1">Documenti di trasporto sincronizzati automaticamente dal gestionale Etiflor.</p>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input placeholder="Cerca per numero, cliente, P.IVA..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="ddt-search-input" />
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="ddt-refresh-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Aggiorna</Button>
          <Badge variant="outline">{data.total} DDT</Badge>
        </div>
        {data.items.length === 0 ? (
          <div className="text-center py-10 text-stone-400" data-testid="ddt-empty">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Nessun DDT importato ancora. Attiva la sincronizzazione in "Sync Etifood (PC)".</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50"><tr>
                <th className="text-left px-3 py-2">Numero</th><th className="text-left px-3 py-2">Data</th>
                <th className="text-left px-3 py-2">Cliente</th><th className="text-right px-3 py-2">Righe</th>
                <th className="text-right px-3 py-2">Imponibile</th><th className="text-right px-3 py-2">Totale</th>
              </tr></thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.id} className="border-t border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => setDetailId(d.id)} data-testid={`ddt-row-${d.id}`}>
                    <td className="px-3 py-2 font-mono font-semibold">{d.numero}{d.serie ? `/${d.serie}` : ""}</td>
                    <td className="px-3 py-2">{fmtDate(d.data)}</td>
                    <td className="px-3 py-2">{d.cliente_ragione_sociale || d.cliente_codice}</td>
                    <td className="px-3 py-2 text-right">{d.num_righe}</td>
                    <td className="px-3 py-2 text-right">{eur(d.totale_imponibile)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[#8A1C2A]">{eur(d.totale_documento)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {detailId && <DdtDetail id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
